<template>
	<div class="timelineEvent relative h-full w-full rounded-sm">
		<div class="h-full w-full rounded-sm">
			<!-- Events without tooltip -->
			<TimelineEventBody
				v-if="!slots.tooltipTitle && !slots.tooltipTitleContent && !slots.tooltipBody"
				:event="event"
				:full-width="fullWidth"
				:simplified="simplified"
			>
				<template v-if="slots.body" #body><slot name="body"></slot></template>
				<template v-if="slots.bodyContent" #bodyContent><slot name="bodyContent"></slot></template>
				<template v-if="slots.depthSuffix" #depthSuffix><slot name="depthSuffix"></slot></template>
			</TimelineEventBody>

			<!-- Events with tooltip -->
			<TimelineTooltip v-else :max-width="tooltipMaxWidth">
				<TimelineEventBody :event="event" :full-width="fullWidth" :simplified="simplified">
					<template v-if="slots.body" #body><slot name="body"></slot></template>
					<template v-if="slots.bodyContent" #bodyContent><slot name="bodyContent"></slot></template>
					<template v-if="slots.depthSuffix" #depthSuffix><slot name="depthSuffix"></slot></template>
				</TimelineEventBody>

				<template v-if="slots.tooltipTitle || slots.tooltipTitleContent" #title>
					<!-- Slot for tooltip's title container -->
					<slot name="tooltipTitle"> </slot>

					<span v-if="slots.tooltipTitleContent" class="text-ink-87">
						<!-- Slot just for tooltip's title text -->
						<slot name="tooltipTitleContent"> </slot>
					</span>
				</template>

				<template v-if="slots.tooltipBody" #content>
					<div class="flex flex-col">
						<!-- Slot for tooltip's body -->
						<slot name="tooltipBody" />

						<!-- Slot for tooltip's footer -->
						<slot v-if="slots.tooltipFooter" name="tooltipFooter" />
					</div>
				</template>

				<template v-else-if="slots.tooltipTitle || slots.tooltipTitleContent" #content>
					<!-- Use default tooltip body if title or title content has been specified -->
					<div class="flex flex-col">
						<div class="flex items-center">
							<span class="mr-02 whitespace-nowrap text-ink-100"><Duration :duration="duration" short /></span>

							<span class="whitespace-nowrap">{{ rangeDescription }}</span>
						</div>

						<!-- Slot for tooltip's footer -->
						<slot v-if="slots.tooltipFooter" name="tooltipFooter" />
					</div>
				</template>
			</TimelineTooltip>
		</div>

		<div
			v-if="hasEnded"
			:class="[`dimmer pointer-events-none absolute bg-level-02 opacity-70 inset-${withDimmerInset ? '[-0.125rem]' : '0'}`]"
		></div>
	</div>
</template>

<script setup lang="ts">
import { useSlots, toRefs } from 'vue'
import Duration from '@/features/eventDetails/components/Duration.vue'
import TimelineEventBody from '@/features/events/components/timeline/events/TimelineEventBody.vue'
import TimelineTooltip from '@/features/events/components/timeline/tooltip/TimelineTooltip.vue'
import { useEvent } from '@/features/events/useEvent'
import type { EventDataWithStore } from '@/types'

const props = withDefaults(
	defineProps<{
		event: EventDataWithStore
		fullWidth?: boolean
		tooltipMaxWidth?: number
		simplified?: boolean
		withDimmerInset?: boolean
	}>(),
	{
		withDimmerInset: true,
		tooltipMaxWidth: undefined
	}
)

const slots = useSlots()

const { event } = toRefs(props)

const { duration, rangeDescription, hasEnded } = useEvent(event)
</script>

<style lang="scss" scoped>
.timelineEvent {
	color: theme('colors.ink.inverted.60');
	border-width: 0.0625rem;
	border-style: solid;
}
</style>
